<template>
  <nav v-if="showPagination" aria-label="Stránkování">
    <div class="d-flex justify-content-between align-items-center">
      <ul class="pagination">
        <li class="page-item" :class="{ 'disabled': currentPage === 1 }">
          <button class="page-link" @click="setFirstPage()" :disabled="currentPage === 1">
            <i class="bi bi-chevron-double-left"></i>
          </button>
        </li>
        <li class="page-item" :class="{ 'disabled': currentPage === 1 }">
          <button class="page-link" @click="moveBackwardPage()" :disabled="currentPage === 1">
            <i class="bi bi-chevron-left"></i>
          </button>
        </li>

        <template v-for="n in getVisiblePages" :key="n">
          <li class="page-item" :class="{ 'active': n === currentPage }">
            <button class="page-link" @click="setCurrentPage(n)">{{ n }}</button>
          </li>
        </template>

        <li class="page-item" :class="{ 'disabled': currentPage === totalPages }">
          <button class="page-link" @click="moveForwardPage()" :disabled="currentPage === totalPages">
            <i class="bi bi-chevron-right"></i>
          </button>
        </li>
        <li class="page-item" :class="{ 'disabled': currentPage === totalPages }">
          <button class="page-link" @click="setLastPage()" :disabled="currentPage === totalPages">
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </li>
      </ul>

      <div class="d-flex align-items-center" v-if="showPerPageSelect">
        <label class="form-label me-2 mb-0">{{ perPageLabel }}:</label>
        <select class="form-select form-select-sm" :value="perPage" @change="onPerPageChange" style="width: auto;">
          <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
        </select>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'

// Props definice
const props = defineProps({
  // Aktuální stránka
  currentPage: {
    type: Number,
    required: true
  },
  // Celkový počet stránek
  totalPages: {
    type: Number,
    required: true
  },
  // Počet položek na stránku
  perPage: {
    type: Number,
    default: 20
  },
  // Možnosti pro výběr počtu položek na stránku
  perPageOptions: {
    type: Array,
    default: () => [10, 20, 50, 100, 500]
  },
  // Zobrazit select pro počet položek na stránku
  showPerPageSelect: {
    type: Boolean,
    default: true
  },
  // Text pro label selectu
  perPageLabel: {
    type: String,
    default: 'Položek na stránku'
  },
  // Maximální počet viditelných stránek v navigaci
  maxVisiblePages: {
    type: Number,
    default: 5
  },
  // Zobrazit stránkování pouze pokud je více než jedna stránka
  hideWhenSinglePage: {
    type: Boolean,
    default: false
  }
})

// Emits definice
const emit = defineEmits([
  'page-changed',     // Změna stránky
  'per-page-changed'  // Změna počtu položek na stránku
])

// Computed properties
const showPagination = computed(() => {
  if (props.hideWhenSinglePage) {
    return props.totalPages > 1
  }
  return true
})

const getVisiblePages = computed(() => {
  const totalPages = props.totalPages
  const current = props.currentPage
  const maxVisible = props.maxVisiblePages

  let start = Math.max(1, current - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  const pages = []
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Methods
const setCurrentPage = (page) => {
  if (page !== props.currentPage && page >= 1 && page <= props.totalPages) {
    emit('page-changed', page)
  }
}

const setFirstPage = () => {
  setCurrentPage(1)
}

const setLastPage = () => {
  setCurrentPage(props.totalPages)
}

const moveForwardPage = () => {
  if (props.currentPage < props.totalPages) {
    setCurrentPage(props.currentPage + 1)
  }
}

const moveBackwardPage = () => {
  if (props.currentPage > 1) {
    setCurrentPage(props.currentPage - 1)
  }
}

const onPerPageChange = (event) => {
  const newPerPage = parseInt(event.target.value)
  if (newPerPage !== props.perPage) {
    emit('per-page-changed', newPerPage)
  }
}
</script>